import {
  Box,
  Button,
  Drawer,
  Flex,
  Grid,
  Input,
  LoadingOverlay,
  Table,
  Tabs,
  TextInput
} from '@mantine/core';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { TbBook2, TbChevronDown, TbLayoutGrid } from 'react-icons/tb';
import { TbSearch } from 'react-icons/tb';
import { FilterInput } from '../../../../../../SemiExtractionDrawer/components/FilterInput';
import { getYearsForLastFiveYears } from '../../../../../../.././../../utils';
import { useLookupDictionaries } from '../../../../../../LookupTables/hooks/useLookupDictionaries';
import extractionStore from '../../../../../../../store';
import {
  FEATURE_FLAG_NAMES,
  useFeatureFlag
} from '../../../../../../../../../hooks/useFeatureFlag';

function groupFilesByWorkflow(files, quater, term, yearFilter) {
  const grouped = {};

  for (const file of files) {
    const workflowName = file.workflow_name;

    const matchesTerm = !term || workflowName.toLowerCase().includes(term.toLowerCase());
    const matchesQuarter = !quater || file.period === quater;
    const matchesYear = !yearFilter || String(file.year) === String(yearFilter);

    if (!matchesTerm || !matchesQuarter || !matchesYear) continue;

    const period = `${file.year} ${file.period}`;

    if (!grouped[workflowName]) {
      grouped[workflowName] = {};
    }

    if (!grouped[workflowName][period]) {
      grouped[workflowName][period] = [];
    }

    // If tables exist, split into multiple objects with table_name
    if (Array.isArray(file.tables) && file.tables.length > 0) {
      for (const table of file.tables) {
        grouped[workflowName][period].push({
          ...file,
          table_name: table
        });
      }
    } else {
      // If no tables, still push the original file (optional — remove if not needed)
      grouped[workflowName][period].push({
        ...file,
        table_name: null
      });
    }
  }

  return grouped;
}

const quaters = ['Q1', 'Q2', 'Q3', 'Q4', 'H1', 'H2', 'Y1', 'X1', 'X2'];
const lastFiveYears = getYearsForLastFiveYears();

export function ExtractionDrawer({
  workflowName,
  period,
  year,
  isOpen,
  onClose,
  replicateExtractionList,
  onSelectExtraction,
  value,
  selectedTableName,
  periodId
}) {
  const [list, setList] = useState({});
  const [quater, setQuater] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [term, setTerm] = useState('');
  const [openedWorflows, setOpenedWorflows] = useState([]);
  const [openedQuaters, setOpenedQuaters] = useState([]);
  const [selectedExtraction, setSelectedExtraction] = useState(null);
  const [selectedExtractionId, setSelectedExtractionId] = useState(null);
  const [selectedExtractionTableName, setSelectedExtractionTableName] = useState(null);

  const [activeTab, setActiveTab] = useState('extracted');

  // Lookup tab state — mirrors LookupSidebar in the Extraction page.
  const [lookupTerm, setLookupTerm] = useState('');
  const [openSystemSection, setOpenSystemSection] = useState(true);
  const [openCustomSection, setOpenCustomSection] = useState(true);
  const [selectedLookup, setSelectedLookup] = useState(null);

  const reportType = extractionStore.reportType;
  const isLookupEnabled = useFeatureFlag(FEATURE_FLAG_NAMES.LOOKUP_TABLE);
  // Skip the hook entirely when the company doesn't have the lookup
  // feature — saves the API call and avoids holding empty lookup state
  // for users who can never see it.
  const lookup = useLookupDictionaries({
    periodId: isLookupEnabled ? periodId : undefined,
    reportType: isLookupEnabled ? reportType : undefined
  });

  const filteredSystemLookups = useMemo(() => {
    const t = lookupTerm.trim().toLowerCase();
    if (!t) return lookup.general;
    return lookup.general.filter(d => d.name.toLowerCase().includes(t));
  }, [lookup.general, lookupTerm]);

  const filteredCustomLookups = useMemo(() => {
    const t = lookupTerm.trim().toLowerCase();
    if (!t) return lookup.custom;
    return lookup.custom.filter(d => d.name.toLowerCase().includes(t));
  }, [lookup.custom, lookupTerm]);

  useEffect(() => {
    if (value === undefined || value === null) return;

    // `value === 0` is the in-memory sentinel for "second table is a
    // lookup". The normalizer remaps it to `extraction_id: null` on the
    // wire. When re-opening the drawer in that state, default to Lookup —
    // unless the company lost the lookup_table flag, in which case stay on
    // the Extracted tab (legacy binding is preserved but read-only).
    if (value === 0) {
      if (isLookupEnabled) setActiveTab('lookup');
    } else {
      setSelectedExtractionId(value);
    }
  }, [value, isLookupEnabled]);

  // Pre-select the lookup dictionary on re-open by name.
  useEffect(() => {
    if (value !== 0 || !selectedTableName || !lookup.all?.length) return;
    const match = lookup.all.find(d => d.name === selectedTableName);
    if (match) setSelectedLookup(match);
  }, [value, selectedTableName, lookup.all]);

  useEffect(() => {
    if (!selectedTableName) return;

    setSelectedExtractionTableName(selectedTableName);
  }, [selectedTableName]);

  useEffect(() => {
    if (!workflowName) return;

    setOpenedWorflows(prev => [...prev, workflowName]);
  }, [workflowName]);

  useEffect(() => {
    if (!period || !workflowName || !isOpen) return;

    function focusElement() {
      if (document.getElementById(workflowName))
        document
          .getElementById(workflowName)
          .scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }

    setQuater(period);
    setYearFilter(String(year));
    setOpenedQuaters(prev => [...prev, `${workflowName}-${year} ${period}`]);

    const timeout = setTimeout(() => focusElement(), 200);

    return () => {
      clearTimeout(timeout);
    };
  }, [period, workflowName, year, isOpen]);

  const handleClose = () => {
    setSelectedExtraction(null);
    onClose();
  };

  useEffect(() => {
    if (replicateExtractionList?.data) {
      const _workflows = groupFilesByWorkflow(
        replicateExtractionList?.data,
        quater,
        term,
        yearFilter
      );
      setList(_workflows);
    }
  }, [replicateExtractionList?.data, quater, term, yearFilter]);

  const onSelectWorflow = key => {
    if (openedWorflows.includes(key)) {
      setOpenedWorflows(prev => prev.filter(val => val !== key));
    } else {
      setOpenedWorflows(prev => [...prev, key]);
    }
  };

  const onSelectQuater = key => {
    if (openedQuaters.includes(key)) {
      setOpenedQuaters(prev => prev.filter(val => val !== key));
    } else {
      setOpenedQuaters(prev => [...prev, key]);
    }
  };

  const onChangeQuater = value => {
    if (value === quater) {
      setQuater('');
    } else {
      setQuater(value);
    }
  };

  const onChangeYear = value => {
    if (value === yearFilter) {
      setYearFilter('');
    } else {
      setYearFilter(value);
    }
  };

  const handleExtractionSelect = () => {
    if (activeTab === 'lookup') {
      if (!selectedLookup) return;
      // In-memory sentinel `0` flags the second table as a lookup; the
      // normalizer remaps it to `extraction_id: null` on the wire. The
      // lookup is identified by its name carried in `then.input` via the
      // parent's `onChangeThen` handler.
      onSelectExtraction(selectedLookup.name, 0);
    } else {
      onSelectExtraction(selectedExtractionTableName, selectedExtractionId);
    }
    onClose();
  };

  const canContinue =
    activeTab === 'lookup'
      ? !!selectedLookup
      : !!selectedExtractionId && !!selectedExtractionTableName;

  return (
    <Drawer
      id="grid-table-extraction-drawer"
      title="Select Source"
      opened={isOpen}
      onClose={onClose}
      position="right"
      size="lg"
      styles={{
        content: { display: 'flex', flexDirection: 'column' },
        body: {
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }
      }}
    >
      {/* Tabs are only meaningful when the lookup feature is enabled. For
          disabled companies the drawer is single-purpose (extraction picker)
          and we skip the tab bar entirely. */}
      {isLookupEnabled && (
        <Tabs id="grid-table-extraction-tabs" value={activeTab} onChange={setActiveTab} mt="8px">
          <Tabs.List grow>
            <Tabs.Tab
              id="grid-table-extraction-tab-extracted"
              value="extracted"
              leftSection={<TbLayoutGrid fontSize="14px" />}
            >
              Extracted
            </Tabs.Tab>
            <Tabs.Tab
              id="grid-table-extraction-tab-lookup"
              value="lookup"
              leftSection={<TbBook2 fontSize="14px" />}
            >
              Lookup
            </Tabs.Tab>
          </Tabs.List>
        </Tabs>
      )}
      {activeTab === 'extracted' && (
        <Flex id="grid-table-extraction-extracted-tab" direction="column" flex={1} mih={0}>
          <TextInput
            id="grid-table-extraction-search"
            leftSection={<TbSearch fontSize="16px" color="#ADB5BD" />}
            placeholder="Search workflows"
            mt="16px"
            value={term}
            onChange={e => setTerm(e.target.value)}
          />

          <Input.Wrapper id="grid-table-extraction-period-wrapper" label="Select Period" mt="10px">
            <Flex align="center" mt="2">
              <Grid w="100%" gutter="sm" grow>
                {quaters.map(value => (
                  <Grid.Col
                    id={`grid-table-extraction-period-${value}`}
                    key={value}
                    span={1}
                    onClick={() => onChangeQuater(value)}
                    data-testid={value}
                  >
                    <Flex
                      id={`grid-table-extraction-period-chip-${value}`}
                      style={{
                        borderRadius: '4px',
                        color: quater === value ? '#fff' : '#000',
                        cursor: 'pointer'
                      }}
                      justify="center"
                      bg={quater === value ? 'blue.6' : 'gray.1'}
                      py="4"
                      fz="sm"
                    >
                      {value}
                    </Flex>
                  </Grid.Col>
                ))}
              </Grid>
            </Flex>
          </Input.Wrapper>

          <FilterInput
            label="Select Year"
            options={lastFiveYears}
            selected={yearFilter}
            onSelect={onChangeYear}
          />

          <Box
            id="grid-table-extraction-results"
            mt="16px"
            pos="relative"
            sx={() => ({
              border: '1px solid #F1F3F5',
              borderRadius: '8px',
              flex: 1,
              minHeight: 0,
              overflow: 'auto'
            })}
          >
            <LoadingOverlay
              id="grid-table-extraction-loading"
              mih={500}
              visible={replicateExtractionList.isLoading}
              zIndex={1000}
              role="presentation"
              data-testid={replicateExtractionList.isLoading && 'loading-overlay'}
            />

            {Object.keys(list).map(workflowName => (
              <Fragment key={workflowName}>
                <Flex
                  sx={() => ({
                    borderBottom: '1px solid #F1F3F5',
                    cursor: 'pointer',
                    position:
                      openedWorflows.includes(workflowName) &&
                      !!openedQuaters.find(el => el.includes(`${workflowName}-`)) &&
                      'sticky',
                    top: 0,
                    backgroundColor: '#fff'
                  })}
                  align="center"
                  p="12px"
                  fz="14px"
                  fw="600"
                  gap="8px"
                  id={workflowName}
                  data-testid={workflowName}
                  onClick={() => onSelectWorflow(workflowName)}
                >
                  <TbChevronDown fontSize="14px" color="#C1C2C5" />
                  {workflowName}
                </Flex>
                {openedWorflows.includes(workflowName) &&
                  Object.keys(list[workflowName]).map(quater => (
                    <Fragment key={`${workflowName}-${quater}`}>
                      <Flex
                        id={`grid-table-extraction-quarter-${workflowName}-${quater}`}
                        sx={() => ({
                          borderBottom: '1px solid #F1F3F5',
                          cursor: 'pointer',
                          position: openedQuaters.includes(`${workflowName}-${quater}`) && 'sticky',
                          top: 46,
                          backgroundColor: '#fff'
                        })}
                        align="center"
                        p="12px 12px 12px 28px"
                        fz="14px"
                        fw="600"
                        gap="8px"
                        data-testid={`${workflowName}-${quater}`}
                        onClick={() => onSelectQuater(`${workflowName}-${quater}`)}
                      >
                        <TbChevronDown fontSize="14px" color="#C1C2C5" />
                        {quater}
                      </Flex>
                      {openedQuaters.includes(`${workflowName}-${quater}`) && (
                        <Table
                          id={`grid-table-extraction-table-${workflowName}-${quater}`}
                          withTableBorder
                          withColumnBorders
                          borderColor="gray.1"
                        >
                          <Table.Thead bg="gray.0">
                            <Table.Tr>
                              <Table.Th c="#495057" fz="12px" fw="600">
                                File name
                              </Table.Th>
                              <Table.Th c="#495057" fz="12px" fw="600">
                                Sheet name
                              </Table.Th>
                              <Table.Th c="#495057" fz="12px" fw="600">
                                Table name
                              </Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {Object.values(list[workflowName][quater])?.map(item => (
                              <Table.Tr
                                id={`grid-table-extraction-row-${item.id}`}
                                onClick={() => {
                                  setSelectedExtractionId(item.id);
                                  setSelectedExtractionTableName(item.table_name);
                                }}
                                key={item.id}
                                bg={
                                  (selectedExtraction?.id || selectedExtractionId) === item.id &&
                                  selectedExtractionTableName === item.table_name
                                    ? 'indigo.0'
                                    : '#fff'
                                }
                                sx={() => ({
                                  cursor: 'pointer',
                                  '&:hover': {
                                    backgroundColor: '#F8F9FA'
                                  }
                                })}
                              >
                                <Table.Td p="18px 12px" fz="12px" c="dark">
                                  {item.file_name}
                                </Table.Td>
                                <Table.Td p="18px 12px" fz="12px" c="dark">
                                  {item.sheet_name}
                                </Table.Td>
                                <Table.Td p="18px 12px" fz="12px" c="dark">
                                  {item.table_name}
                                </Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      )}
                    </Fragment>
                  ))}
              </Fragment>
            ))}
          </Box>
        </Flex>
      )}

      {activeTab === 'lookup' && (
        <Flex id="grid-table-lookup-tab" direction="column" flex={1} mih={0}>
          <TextInput
            id="grid-table-lookup-search"
            leftSection={<TbSearch fontSize="16px" color="#ADB5BD" />}
            placeholder="Search lookup tables..."
            mt="16px"
            value={lookupTerm}
            onChange={e => setLookupTerm(e.target.value)}
          />

          <Box
            id="grid-table-lookup-results"
            mt="16px"
            pos="relative"
            sx={() => ({
              border: '1px solid #F1F3F5',
              borderRadius: '8px',
              flex: 1,
              minHeight: 0,
              overflow: 'auto'
            })}
          >
            <LoadingOverlay
              id="grid-table-lookup-loading"
              visible={lookup.isLoading}
              zIndex={1000}
              role="presentation"
            />

            {filteredSystemLookups.length > 0 && (
              <>
                <Flex
                  id="grid-table-lookup-system-header"
                  sx={() => ({
                    borderBottom: '1px solid #F1F3F5',
                    cursor: 'pointer',
                    position: 'sticky',
                    top: 0,
                    backgroundColor: '#fff'
                  })}
                  align="center"
                  p="12px"
                  fz="14px"
                  fw="600"
                  gap="8px"
                  onClick={() => setOpenSystemSection(prev => !prev)}
                >
                  <TbChevronDown
                    fontSize="14px"
                    color="#C1C2C5"
                    style={{
                      transform: openSystemSection ? 'rotate(0deg)' : 'rotate(-90deg)',
                      transition: 'transform 0.15s ease'
                    }}
                  />
                  System Dictionaries
                </Flex>
                {openSystemSection &&
                  filteredSystemLookups.map(dict => (
                    <Flex
                      id={`grid-table-lookup-system-item-${dict.id}`}
                      key={dict.id}
                      align="center"
                      gap="8px"
                      p="10px 12px 10px 32px"
                      fz="13px"
                      onClick={() => setSelectedLookup(dict)}
                      bg={selectedLookup?.id === dict.id ? 'indigo.0' : '#fff'}
                      sx={() => ({
                        cursor: 'pointer',
                        borderBottom: '1px solid #F1F3F5',
                        '&:hover': {
                          backgroundColor: selectedLookup?.id === dict.id ? undefined : '#F8F9FA'
                        }
                      })}
                    >
                      <TbBook2 fontSize="14px" color="#ADB5BD" />
                      {dict.name}
                    </Flex>
                  ))}
              </>
            )}

            {filteredCustomLookups.length > 0 && (
              <>
                <Flex
                  id="grid-table-lookup-custom-header"
                  sx={() => ({
                    borderBottom: '1px solid #F1F3F5',
                    cursor: 'pointer',
                    position: 'sticky',
                    top: 0,
                    backgroundColor: '#fff'
                  })}
                  align="center"
                  p="12px"
                  fz="14px"
                  fw="600"
                  gap="8px"
                  onClick={() => setOpenCustomSection(prev => !prev)}
                >
                  <TbChevronDown
                    fontSize="14px"
                    color="#C1C2C5"
                    style={{
                      transform: openCustomSection ? 'rotate(0deg)' : 'rotate(-90deg)',
                      transition: 'transform 0.15s ease'
                    }}
                  />
                  Custom Lookups
                </Flex>
                {openCustomSection &&
                  filteredCustomLookups.map(dict => (
                    <Flex
                      id={`grid-table-lookup-custom-item-${dict.id}`}
                      key={dict.id}
                      align="center"
                      gap="8px"
                      p="10px 12px 10px 32px"
                      fz="13px"
                      onClick={() => setSelectedLookup(dict)}
                      bg={selectedLookup?.id === dict.id ? 'indigo.0' : '#fff'}
                      sx={() => ({
                        cursor: 'pointer',
                        borderBottom: '1px solid #F1F3F5',
                        '&:hover': {
                          backgroundColor: selectedLookup?.id === dict.id ? undefined : '#F8F9FA'
                        }
                      })}
                    >
                      <TbBook2 fontSize="14px" color="#ADB5BD" />
                      {dict.name}
                    </Flex>
                  ))}
              </>
            )}
          </Box>
        </Flex>
      )}

      <Flex align="center" mt="16px" justify="space-between">
        <Button size="xs" onClick={handleClose} variant="default" color="dark">
          Cancel
        </Button>

        <Flex align="center" gap="16px">
          <Button disabled={!canContinue} size="xs" onClick={handleExtractionSelect}>
            Continue
          </Button>
        </Flex>
      </Flex>
    </Drawer>
  );
}
